import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Decimal, ORDER_LIMITS } from '@app/shared';
import { loggedOut, sessionCleared } from '@/shared/lib/redux/actions';
import { isNonPositiveQty, qtyStep, sanitizeQty } from './cart.logic';
import type { CartLineSnapshot, CartState } from './types';

export interface AddLinePayload {
  storeId: string;
  productId: string;
  snapshot: CartLineSnapshot;
  /** Amount to add on top of what is already in the cart; defaults to one step. */
  qty?: string;
}

// Starts empty: the carts are per user and are hydrated from storage once the signed-in user is
// known (`cartHydrated`, dispatched by app/store/listeners on `credentialsUpdated`).
const initialState: CartState = {};

function addTo(state: CartState, { storeId, productId, snapshot, qty }: AddLinePayload): void {
  const amount = sanitizeQty(qty ?? qtyStep(snapshot.allowFractional), snapshot.allowFractional);
  if (!amount) return;
  const cart = (state[storeId] ??= {});
  const existing = cart[productId];
  if (!existing && Object.keys(cart).length >= ORDER_LIMITS.MAX_ITEMS) return;
  const next = sanitizeQty(
    new Decimal(existing?.qty ?? 0).plus(amount).toFixed(),
    snapshot.allowFractional,
  );
  // Always refresh the snapshot — it carries the latest price/name the client has seen.
  if (next) cart[productId] = { qty: next, snapshot };
}

function dropIfEmpty(state: CartState, storeId: string): void {
  const cart = state[storeId];
  if (cart && Object.keys(cart).length === 0) delete state[storeId];
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    lineAdded: (state, action: PayloadAction<AddLinePayload>) => {
      addTo(state, action.payload);
      dropIfEmpty(state, action.payload.storeId);
    },
    /** Bulk add (e.g. "repeat order"): quantities are summed with what is already there. */
    linesAdded: (state, action: PayloadAction<{ storeId: string; lines: AddLinePayload[] }>) => {
      for (const line of action.payload.lines)
        addTo(state, { ...line, storeId: action.payload.storeId });
      dropIfEmpty(state, action.payload.storeId);
    },
    /** Sets an exact quantity. Zero/negative removes the line; unparsable input is ignored. */
    qtySet: (state, action: PayloadAction<{ storeId: string; productId: string; qty: string }>) => {
      const { storeId, productId, qty } = action.payload;
      const line = state[storeId]?.[productId];
      if (!line) return;
      const next = sanitizeQty(qty, line.snapshot.allowFractional);
      if (next) {
        line.qty = next;
      } else if (isNonPositiveQty(qty)) {
        delete state[storeId]?.[productId];
        dropIfEmpty(state, storeId);
      }
    },
    lineRemoved: (state, action: PayloadAction<{ storeId: string; productId: string }>) => {
      const { storeId, productId } = action.payload;
      delete state[storeId]?.[productId];
      dropIfEmpty(state, storeId);
    },
    /** Replaces an existing line's snapshot and quantity with fresh catalog data (checkout retry). */
    lineRefreshed: (
      state,
      action: PayloadAction<{
        storeId: string;
        productId: string;
        snapshot: CartLineSnapshot;
        qty: string;
      }>,
    ) => {
      const { storeId, productId, snapshot, qty } = action.payload;
      const cart = state[storeId];
      const next = sanitizeQty(qty, snapshot.allowFractional);
      if (cart?.[productId] && next) cart[productId] = { qty: next, snapshot };
    },
    /** Empties one store's cart; other stores' carts are untouched. */
    cartCleared: (state, action: PayloadAction<{ storeId: string }>) => {
      delete state[action.payload.storeId];
    },
    /** Replaces the in-memory carts with the ones persisted for the user who just signed in. */
    cartHydrated: (_state, action: PayloadAction<CartState>) => action.payload,
  },
  extraReducers: (builder) => {
    // One account's carts are never shown to the next one: the in-memory carts are dropped whenever
    // the session ends. Storage is left alone here — an expired session keeps the user's cart on
    // disk; only the explicit `loggedOut` erases it (the persistence subscriber in app/store sees
    // the emptied state while the user is still known and removes that user's entry).
    builder.addCase(sessionCleared, () => ({})).addCase(loggedOut, () => ({}));
  },
});

export const {
  lineAdded,
  linesAdded,
  qtySet,
  lineRemoved,
  lineRefreshed,
  cartCleared,
  cartHydrated,
} = cartSlice.actions;
export const cartReducer = cartSlice.reducer;
